/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// mantine
import { Box, Paper, Group, Image } from '@mantine/core';

// styles
import classes from './measurabilities.module.css';

// components
import {
	ConfirmationModal,
	MenuFilter,
} from '@components/molecules';
import {
	AddFiltersButton,
	Button,
	Pagination,
} from '@components/atoms';

// i18n
import { useTranslation } from 'react-i18next';

// icons
import { IconCirclePlus } from '@tabler/icons-react';
import ArrowScrollIcon from '@assets/icons/arrow-scroll.svg';

// configs
import { tableSchemaBase as columns } from '@features/resources/measurabilities/measurabilities.columns';

// hooks
import { useMeasurabilities } from '@features/resources/measurabilities/hooks/useMeasurabilities';
import { Datatable } from '@components/organisms';

// types
import {
	TMeasurabilityProjectPopulated,
	TTargetColumn,
} from '@global-types/index';
import { useGetMeasurabilities } from '@features/resources/measurabilities/api/get-measurabilities';
import {
	convertArrayToLabelValue,
	getVisibleColumns,
} from '@utils/functions';
import { useOrganizations } from 'shared/api/get-organizations';
import { useProducers } from 'shared/api/get-producers';
import { PROJECT_TECHNOLOGIES } from '@config/constants/project-technologies';
import { useCountryCodes } from 'shared/api/get-country-codes';
import { EUROPEAN_COUNTRIES } from '@config/constants/european-countries';
import isNil from 'lodash/isNil';
import { useRemove } from '@features/resources/measurabilities/api/remove';
import { DrawerMeasurabilityProjectShowMore } from '@features/resources/measurabilities/components/DrawerMeasurabilityProjectShowMore';
import { useActivateMeasurabilityProject } from '@features/resources/measurabilities/api/activate-measurability-project';
import { AddMeasurabilityProject } from '@features/resources/measurabilities/components/AddMeasurabilityProject';
import { UpdateMeasurabilityProject } from '@features/resources/measurabilities/components/UpdateMeasurabilityProject';
import { ACTIVATED_FILTER_OPTIONS } from '@utils/options';
import { TMeasurabilityProject } from '@global-types/api/api';
import {
	Authorization,
	useAuthorization,
} from '@lib/authorization';
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const Measurabilities = () => {
	/* ******************************** HOOKS ******************************* */
	const { t } = useTranslation();

	const {
		handleActiveFilter,
		isOverflowFilters,
		ref,
		containerRef,
		showScrollFilters,
		handleScroll,
		handleAddItem,
		handleOrganizationFilter,
		handleViewMore,
		handleRowClicked,
		handleSortStatusChange,
		measurabilitiesPage,
		measurabilitiesSortStatus,
		measurabilitiesPageSize,
		handlePaginationChangeSize,
		handleChangePagination,
		measurabilitiesQueryParams,
		handleProducerFilter,
		handleTechnologyFilter,
		handleCountryFilter,
		measurabilitiesSortParams,
		handleCloseConfirmationDeleteItem,
		measurabilitiesOpenConfirmationModalDelete,
		handleConfirmDeleteItem,
		setMeasurabilitiesOpenConfirmationModalDelete,
		openDrawer,
		setOpenDrawer,
		measurabilitiesSelectedRow,
		setAddMeasurabilityProjectFormOpened,
		addMeasurabilityProjectFormOpened,
		setUpdateMeasurabilityProjectFormOpened,
		updateMeasurabilityProjectFormOpened,
		setMeasurabilitiesDatatableOptions,
		measurabilitiesDatatableOptions,
		setMeasurabilitiesSelectedRow,
		queryParamsOrganizations,
		resetFilterProducers,

		setResetFilterProducers,
	} = useMeasurabilities();

	const { data, isFetching, total, totalPages } =
		useGetMeasurabilities(
			measurabilitiesPage,
			measurabilitiesPageSize,
			measurabilitiesQueryParams,
			measurabilitiesSortParams
		);

	// APIs
	const { data: organizations } = useOrganizations();

	const { data: producers } = useProducers(
		queryParamsOrganizations
	);

	const { data: countryCodes } = useCountryCodes(
		'measurabilities'
	);

	const remove = useRemove(
		measurabilitiesPage,
		measurabilitiesPageSize,
		measurabilitiesQueryParams,
		measurabilitiesSortParams
	);

	const filteredCountries =
		!isNil(countryCodes) && Array.isArray(countryCodes)
			? EUROPEAN_COUNTRIES.filter((item) =>
					countryCodes.includes(item.value)
				)
			: [];

	const { mutate } = useActivateMeasurabilityProject(
		measurabilitiesPage,
		measurabilitiesPageSize,
		measurabilitiesQueryParams,
		measurabilitiesSortParams
	);

	const { checkAcl, checkAccess } = useAuthorization();

	return (
		<Box className={classes.container} ref={ref}>
			{/*Add project */}
			<AddMeasurabilityProject
				onClose={() => {
					setAddMeasurabilityProjectFormOpened(false);
				}}
				opened={addMeasurabilityProjectFormOpened}
			/>
			{/*Update project */}
			<UpdateMeasurabilityProject
				onClose={() => {
					setUpdateMeasurabilityProjectFormOpened(false);
				}}
				opened={updateMeasurabilityProjectFormOpened}
				projectId={measurabilitiesSelectedRow.id}
			/>
			{/*View more */}
			<DrawerMeasurabilityProjectShowMore
				handleclose={() => {
					setOpenDrawer(false);
				}}
				opened={openDrawer}
				data={measurabilitiesSelectedRow}
				position={'right'}
				handleactive={async (status: boolean) => {
					await mutate({
						active: status,
						ids: [measurabilitiesSelectedRow.id],
					});
				}}
				handledelete={() => {
					setMeasurabilitiesOpenConfirmationModalDelete({
						open: true,
						params: measurabilitiesSelectedRow,
					});
				}}
				handleupdate={() => {
					setUpdateMeasurabilityProjectFormOpened(true);
					setOpenDrawer(false);
				}}
			/>

			{/*Delete confirmation modal */}
			<ConfirmationModal
				title={t(
					'measurabilities.alert.confirmationDelete.title'
				)}
				description={t(
					'measurabilities.alert.confirmationDelete.description'
				)}
				onConfirm={() => {
					handleConfirmDeleteItem(remove);
					if (openDrawer) {
						setOpenDrawer(false);
					}
				}}
				onClose={handleCloseConfirmationDeleteItem}
				opened={
					measurabilitiesOpenConfirmationModalDelete &&
					measurabilitiesOpenConfirmationModalDelete.open
				}
			/>
			<Paper className={classes.paper}>
				{/* Row filters */}
				<Box className={classes.containerFilters}>
					<Box className={classes.wrapperRowLeft}>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('left');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToLeft
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
						<Box
							ref={containerRef}
							className={classes.wrapperFilters}
						>
							<Group gap={'0px'}>
								<Box
									className={classes.containerFilters5}
								>
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											onApply={handleActiveFilter}
											label={t('global.status')}
											size="lg"
											options={ACTIVATED_FILTER_OPTIONS}
											placeholderSearch={t(
												'filters.placeholderSearch.searchStatus'
											)}
											placeholder={t(
												'filters.placeholders.selectStatus'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Authorization
										allowedRoles={[
											ROLES.ADMIN,
											ROLES.SUPERADMIN,
											ROLES.STAFF,
										]}
									>
										<Box
											className={
												classes.containerFilterSelect
											}
										>
											<MenuFilter
												onApply={handleOrganizationFilter}
												label="organizations"
												size="lg"
												options={convertArrayToLabelValue(
													organizations
												)}
												placeholderSearch={t(
													'filters.placeholderSearch.searchOrganization'
												)}
												placeholder={t(
													'filters.placeholders.selectOrganization'
												)}
											/>
										</Box>
									</Authorization>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											reset={resetFilterProducers}
											handlereset={(
												_isReset: boolean
											) => {
												setResetFilterProducers(_isReset);
											}}
											onApply={handleProducerFilter}
											label={t('global.producer')}
											size="lg"
											options={convertArrayToLabelValue(
												producers
											)}
											placeholderSearch={t(
												'filters.placeholderSearch.searchProducer'
											)}
											placeholder={t(
												'filters.placeholders.selectProducer'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											value={
												measurabilitiesQueryParams[
													'location.countryCode'
												]
													? measurabilitiesQueryParams[
															'location.countryCode'
														]['$in']
													: []
											}
											isPersist
											onApply={handleCountryFilter}
											label={t('global.country')}
											size="lg"
											options={filteredCountries}
											placeholderSearch={t(
												'filters.placeholderSearch.searchCountry'
											)}
											placeholder={t(
												'filters.placeholders.selectCountry'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											isPersist
											value={
												measurabilitiesQueryParams.technology
													? measurabilitiesQueryParams.technology
													: []
											}
											onApply={handleTechnologyFilter}
											label="Project type"
											size="lg"
											options={PROJECT_TECHNOLOGIES}
											placeholderSearch={t(
												'filters.placeholderSearch.searchTechnology'
											)}
											placeholder={t(
												'filters.placeholders.selectTechnology'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterNumeric
										}
									>
										<AddFiltersButton>
											{t('global.addFilters')}
										</AddFiltersButton>
									</Box>
								</Box>
							</Group>
						</Box>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('right');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToRight
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
					</Box>
					{checkAcl(ACLS.MEASURABILITIES.CREATE) &&
						checkAccess({
							allowedRoles: [
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							],
						}) && (
							<div className={classes.wrapperRowRight}>
								<Button
									leftSection={
										<IconCirclePlus size={23} />
									}
									hiddenFrom="xxl"
									size="md"
									onClick={handleAddItem}
								>
									{t('global.add') +
										' ' +
										t('global.project')}
								</Button>
								<Button
									leftSection={
										<IconCirclePlus size={23} />
									}
									visibleFrom="xxl"
									size="lg"
									onClick={handleAddItem}
								>
									{t('global.add') +
										' ' +
										t('global.project')}
								</Button>
							</div>
						)}
				</Box>
				<Box className={classes.spacerRow} />
				<Box className={classes.spacerRow} />
				{/* Datatable */}
				<Box className={classes.containerDatatable}>
					<Datatable
						onActive={async (
							row: TMeasurabilityProjectPopulated
						) => {
							await mutate({
								active: !row.active,
								ids: [row.id],
							});
						}}
						handleUpdateColumns={(
							cols: TTargetColumn[]
						) => {
							setMeasurabilitiesDatatableOptions(
								getVisibleColumns(columns, cols)
							);
						}}
						loading={
							isFetching
								? isFetching.toString()
								: undefined
						}
						allColumns={columns}
						columns={measurabilitiesDatatableOptions}
						data={data}
						onRowClick={handleRowClicked}
						onEditItem={(row: TMeasurabilityProject) => {
							setMeasurabilitiesSelectedRow(row);
							setUpdateMeasurabilityProjectFormOpened(
								true
							);
						}}
						onViewMore={handleViewMore}
						selectedRecords={null}
						onRemoveItem={(
							row: TMeasurabilityProject
						) => {
							setMeasurabilitiesOpenConfirmationModalDelete(
								{
									open: true,
									params: row,
								}
							);
						}}
						style={{
							minHeight: '100%',
						}}
						height={250}
						onSortStatusChange={handleSortStatusChange}
						sortstatus={measurabilitiesSortStatus}
					/>
				</Box>
			</Paper>
			{/* Pagination */}
			<Box className={classes.containerPagination}>
				<Pagination
					pagesize={measurabilitiesPageSize}
					totalitems={total}
					activepage={measurabilitiesPage}
					handlechangesize={handlePaginationChangeSize}
					onChange={handleChangePagination}
					total={totalPages ? totalPages : 0}
				/>
			</Box>
		</Box>
	);
};

export default Measurabilities;
